import { isEthereumWallet } from "@dynamic-labs/ethereum";
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import { useTranslation } from "react-i18next";
import { encodeFunctionData } from "viem";

import { toastInstance } from "../ToastConfig";

import {
  TOKENS_CONTRACTS,
  TOKENS_CHAIN_ID,
  TOKENS_PRECISION,
} from "@/utils/web3/Tokens";
import { ABI_ERC_20 } from "@/utils/web3/abis/Erc20";

function convertAmountToPrecision(amount: number, precision: number) {
  return BigInt(amount * Math.pow(10, precision));
}

export const useSendTransaction = () => {
  const { primaryWallet } = useDynamicContext();
  const { t } = useTranslation();

  if (!primaryWallet || !isEthereumWallet(primaryWallet)) {
    return { error: "No wallet found" };
  }
  const currentChain = primaryWallet?.getNetwork();
  const walletClient = primaryWallet?.getWalletClient();

  const sendTransaction = async (
    sourceAddress: string,
    destinationAddress: string,
    chain: string,
    token: string,
    amount: number
  ) => {
    const chainFormatted = chain.toLowerCase();
    const tokenFormatted = token.toLowerCase();
    if (
      typeof currentChain === "number" &&
      currentChain !== TOKENS_CHAIN_ID[chain]
    ) {
      await primaryWallet.switchNetwork(TOKENS_CHAIN_ID[chain]);
    }

    const tokenAddress = TOKENS_CONTRACTS[tokenFormatted][chainFormatted] ?? "";
    const tokenPrecision = TOKENS_PRECISION[tokenFormatted] ?? 18;

    //Convert amount to bigint with specified precision
    const transactionValue = convertAmountToPrecision(amount, tokenPrecision);

    if (!tokenAddress) {
      toastInstance("error", t("common:toast.error.subtitle.tokennotfound"));
      return;
    }

    const transactionContract = {
      abi: ABI_ERC_20,
      functionName: "transfer",
      target: tokenAddress,
      args: [destinationAddress, transactionValue],
    };

    // @ts-ignore
    const encodedContractData = encodeFunctionData(transactionContract);

    try {
      // @ts-ignore
      const hash = await (
        await walletClient
      )
        // @ts-ignore
        .sendTransaction({
          to: tokenAddress,
          data: encodedContractData,
        });

      return hash;
    } catch (error) {
      console.log("error", error);
      throw new Error("Error sending transaction");
    }
  };

  return { sendTransaction };
};

export const createTronTransaction = async (
  sourceAddress: string,
  destinationAddress: string,
  token: string,
  amount: number,
  memo: string
) => {
  // @ts-ignore
  const tronWeb = window.tronLink.tronWeb;
  const tokenFormatted = token.toLowerCase();

  const tokenAddress = TOKENS_CONTRACTS[tokenFormatted]["tron"] ?? "";
  const transactionValue = convertAmountToPrecision(amount, 6);

  const rawTransaction = await tronWeb.transactionBuilder
    .triggerSmartContract(
      tokenAddress,
      "transfer(address,uint256)",
      {},
      [
        {
          type: "address",
          value: destinationAddress,
        },
        {
          type: "uint256",
          value: transactionValue,
        },
      ],
      sourceAddress
    )
    .then(async (transaction: any) => {
      const transactionWithMemo =
        await tronWeb.transactionBuilder.addUpdateData(
          transaction.transaction,
          memo,
          "utf8"
        );
      return transactionWithMemo;
    });

  try {
    const signedTransaction = await tronWeb.trx.sign(rawTransaction);

    if (signedTransaction) {
      await tronWeb.trx.sendRawTransaction(signedTransaction);
    }
  } catch (error) {
    console.error("error", error);
  }
};
